import { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useProject } from "./ProjectLayout";
import api from "@/lib/api";
import { QuestionRenderer } from "@/components/QuestionRenderer";
import { isVisible, visibleQuestions, isAnswered, formatValue, SCOPE_META, SCOPE_ORDER, EXPANSION_STATUS, detectExpansion, scopeLabel } from "@/lib/discovery";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import {
  BookOpen, ArrowRight, ArrowLeft, HelpCircle, PlayCircle, CheckCircle2,
  Lightbulb, X, ClipboardCheck, Pencil, Check, Save, AlertTriangle, StickyNote, Trash2, Plus, Ban, Flag, Layers, Sparkles, Building2,
} from "lucide-react";

const PHASE = { LEARN: "learn", DISCOVER: "discover", REVIEW: "review" };

function AddQuestionDialog({ projectId, module, onAdded }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [type, setType] = useState("long_text");
  const [help, setHelp] = useState("");
  const [optionsText, setOptionsText] = useState("");
  const [busy, setBusy] = useState(false);
  const needsOptions = type === "single_select" || type === "multi_select";
  const [minScope, setMinScope] = useState("standard");
  const submit = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      const options = needsOptions ? optionsText.split(",").map((s) => s.trim()).filter(Boolean) : null;
      await api.post(`/projects/${projectId}/custom-questions`, { module, text, type, help_text: help, options, min_scope: minScope });
      toast.success("Question added to this module");
      setOpen(false); setText(""); setHelp(""); setOptionsText(""); setType("long_text"); setMinScope("standard");
      onAdded();
    } catch { toast.error("Could not add question"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid="add-question-btn" className="rounded-lg"><Plus className="mr-1 h-4 w-4" /> Add a question</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Add a question to this module</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><Label className="text-sm font-semibold">Question</Label><Textarea data-testid="cq-text" value={text} onChange={(e) => setText(e.target.value)} className="mt-1 rounded-lg" placeholder="What would you like to ask?" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label className="text-sm font-semibold">Answer type</Label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger data-testid="cq-type" className="mt-1 rounded-lg"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="long_text">Long text</SelectItem>
                  <SelectItem value="short_text">Short text</SelectItem>
                  <SelectItem value="yes_no">Yes / No</SelectItem>
                  <SelectItem value="single_select">Single select</SelectItem>
                  <SelectItem value="multi_select">Multi select</SelectItem>
                  <SelectItem value="number">Number</SelectItem>
                  <SelectItem value="date">Date</SelectItem>
                  <SelectItem value="hierarchy">Hierarchy</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-sm font-semibold">Minimum scope</Label>
              <Select value={minScope} onValueChange={setMinScope}>
                <SelectTrigger data-testid="cq-min-scope" className="mt-1 rounded-lg"><SelectValue /></SelectTrigger>
                <SelectContent>{SCOPE_ORDER.map((s) => <SelectItem key={s} value={s}>{SCOPE_META[s].label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          {needsOptions && <div><Label className="text-sm font-semibold">Options (comma-separated)</Label><Input data-testid="cq-options" value={optionsText} onChange={(e) => setOptionsText(e.target.value)} className="mt-1 rounded-lg" placeholder="Option A, Option B, Option C" /></div>}
          <div><Label className="text-sm font-semibold">Help text (optional)</Label><Input data-testid="cq-help" value={help} onChange={(e) => setHelp(e.target.value)} className="mt-1 rounded-lg" placeholder="Why are we asking this?" /></div>
        </div>
        <DialogFooter>
          <Button data-testid="cq-submit" onClick={submit} disabled={busy} className="rounded-lg bg-sky-600 hover:bg-sky-700">{busy ? "Adding…" : "Add question"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ScopeExpansionNotice({ flag, canManage, onClassify }) {
  const st = EXPANSION_STATUS[flag.status] || EXPANSION_STATUS.pending;
  const OPTIONS = [["approved_in_scope", "Approve In-Scope"], ["change_request", "Change Request"], ["future_backlog", "Future Backlog"]];
  return (
    <div data-testid={`expansion-flag-${flag.question_key}`} className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-4">
      <div className="flex items-start gap-2.5">
        <Flag className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
        <div className="flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-heading text-sm font-bold text-amber-800">Potential Scope Expansion</span>
            <StatusBadge label={st.label} tone={st.tone} />
          </div>
          <p className="mt-1 text-xs leading-relaxed text-amber-800">
            "{flag.trigger_label}" may go beyond the agreed <strong>{scopeLabel(flag.detected_scope)}</strong>-level scope for this area. This does not block discovery — a consultant can decide how to handle it.
          </p>
          {canManage && (
            <div className="mt-3 flex flex-wrap gap-2">
              {OPTIONS.map(([val, label]) => (
                <Button key={val} size="sm" variant={flag.status === val ? "default" : "outline"} data-testid={`classify-${flag.question_key}-${val}`}
                  onClick={() => onClassify(flag.question_key, val)}
                  className={cn("h-8 rounded-lg text-xs", flag.status === val && "bg-amber-600 hover:bg-amber-700")}>
                  {label}
                </Button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function AiSuggestDialog({ projectId, module, onAdded }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [drafts, setDrafts] = useState([]);
  const generate = async () => {
    setLoading(true);
    try {
      const r = await api.post(`/projects/${projectId}/custom-modules/suggest-questions`, { title: module.title, description: module.description, scope: module.scope });
      setDrafts((r.data.questions || []).map((q) => ({ ...q, _include: true })));
    } catch { toast.error("Could not generate suggestions"); }
    finally { setLoading(false); }
  };
  const openDialog = () => { setOpen(true); setDrafts([]); generate(); };
  const setField = (i, f, v) => setDrafts((d) => d.map((x, idx) => (idx === i ? { ...x, [f]: v } : x)));
  const addAll = async () => {
    const chosen = drafts.filter((d) => d._include).map(({ _include, ...q }) => q);
    if (chosen.length === 0) { toast.error("Select at least one question"); return; }
    setBusy(true);
    try {
      await api.post(`/projects/${projectId}/custom-modules/${module.key}/questions`, { questions: chosen });
      toast.success(`${chosen.length} question${chosen.length > 1 ? "s" : ""} added`);
      setOpen(false); setDrafts([]); onAdded();
    } catch { toast.error("Could not add questions"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button variant="outline" size="sm" data-testid="ai-suggest-btn" onClick={openDialog} className="rounded-lg border-violet-200 text-violet-700 hover:bg-violet-50">
        <Sparkles className="mr-1.5 h-4 w-4" /> AI-suggest questions
      </Button>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
        <DialogHeader><DialogTitle>AI-suggested questions for this module</DialogTitle></DialogHeader>
        {loading ? (
          <div className="py-16 text-center text-sm text-slate-400">Generating draft questions…</div>
        ) : (
          <>
            <p className="text-sm text-slate-500">Review and edit these drafts, untick any you don't want, then add them. They respect the module's current scope.</p>
            <div className="mt-3 space-y-3">
              {drafts.map((d, i) => (
                <div key={i} data-testid={`ai-draft-${i}`} className="rounded-xl border border-slate-200 p-3">
                  <div className="flex items-start gap-2">
                    <input type="checkbox" data-testid={`ai-draft-include-${i}`} checked={d._include} onChange={(e) => setField(i, "_include", e.target.checked)} className="mt-1.5 h-4 w-4" />
                    <div className="flex-1 space-y-2">
                      <Textarea data-testid={`ai-draft-text-${i}`} value={d.text} onChange={(e) => setField(i, "text", e.target.value)} className="min-h-[44px] rounded-lg text-sm" />
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="rounded bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-500">{d.type}</span>
                        <span className="rounded bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-500">{d.section}</span>
                        <Select value={d.min_scope} onValueChange={(v) => setField(i, "min_scope", v)}>
                          <SelectTrigger className="h-7 w-40 rounded-lg text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>{SCOPE_ORDER.map((s) => <SelectItem key={s} value={s}>{SCOPE_META[s].label}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              {drafts.length === 0 && <div className="py-8 text-center text-sm text-slate-400">No suggestions returned. Try again or add questions manually.</div>}
            </div>
          </>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={generate} disabled={loading || busy} className="rounded-lg">Regenerate</Button>
          <Button data-testid="ai-add-questions" onClick={addAll} disabled={loading || busy || drafts.length === 0} className="rounded-lg bg-violet-600 hover:bg-violet-700">
            {busy ? "Adding…" : `Add ${drafts.filter((d) => d._include).length} question(s)`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function OrgContextBox({ data, responses }) {
  const org = data?.modules?.find((m) => m.key === "org_structure");
  if (!org || org.out_of_scope) return null;
  const wanted = [
    ["os_teams", "Teams"], ["os_division", "Structure"],
    ["os_sales_hierarchy", "Sales hierarchy"], ["os_service_hierarchy", "Service hierarchy"],
    ["os_visibility", "Default visibility"],
  ];
  const rows = [];
  wanted.forEach(([key, label]) => {
    const q = org.questions.find((x) => x.key === key);
    if (q && isAnswered(responses[key])) rows.push({ label, value: formatValue(q, responses[key]) });
  });
  if (rows.length === 0) return null;
  return (
    <div data-testid="org-context-box" className="mb-6 rounded-2xl border border-sky-200 bg-sky-50/60 p-5">
      <div className="flex items-center gap-2 text-sky-700"><Building2 className="h-4 w-4" /><span className="font-heading text-sm font-bold">From your Org & User Structure</span></div>
      <p className="mt-1 text-xs text-slate-500">Captured earlier — use this as context when answering assignment, ownership and visibility questions here.</p>
      <div className="mt-3 grid gap-2 sm:grid-cols-2">
        {rows.map((r) => (
          <div key={r.label} className="rounded-lg bg-white/70 p-2.5 text-xs">
            <div className="font-semibold text-slate-500">{r.label}</div>
            <div className="mt-0.5 text-slate-700">{r.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}


function ModuleScopeControl({ value, projectDefault, onChange, testid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="h-9 w-auto gap-1.5 rounded-lg border-sky-200 bg-sky-50/60 text-xs font-semibold text-sky-700">
        <Layers className="h-3.5 w-3.5 text-sky-500" /> <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="inherit">Inherit ({SCOPE_META[projectDefault]?.label || projectDefault})</SelectItem>
        {SCOPE_ORDER.map((s) => <SelectItem key={s} value={s}>{SCOPE_META[s].label}</SelectItem>)}
        <SelectItem value="out_of_scope">Out of Scope</SelectItem>
      </SelectContent>
    </Select>
  );
}

export default function GuidedModule() {
  const { id, moduleKey } = useParams();
  const navigate = useNavigate();
  const { refresh } = useProject();
  const { user } = useAuth();
  const canManage = user?.role === "admin" || user?.role === "consultant";
  const [data, setData] = useState(null);
  const [responses, setResponses] = useState({});
  const [notes, setNotes] = useState({});
  const [flags, setFlags] = useState({});
  const [phase, setPhase] = useState(PHASE.LEARN);
  const [editingFromReview, setEditingFromReview] = useState(false);
  const [idx, setIdx] = useState(0);
  const [savedAt, setSavedAt] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const dirtyRef = useRef({});
  const noteDirtyRef = useRef({});
  const timerRef = useRef(null);

  const loadDiscovery = useCallback(() => api.get(`/projects/${id}/discovery`).then((r) => {
    setData(r.data);
    setResponses(r.data.responses || {});
    setNotes(r.data.notes || {});
    setFlags(r.data.scope_flags_by_question || {});
  }), [id]);
  useEffect(() => { loadDiscovery(); }, [loadDiscovery]);

  const module = useMemo(() => data?.modules.find((m) => m.key === moduleKey), [data, moduleKey]);
  const vQs = useMemo(() => (module ? visibleQuestions(module, responses) : []), [module, responses]);
  const current = vQs[idx];

  const flushSaves = useCallback(async () => {
    const ans = Object.entries(dirtyRef.current);
    const nts = Object.entries(noteDirtyRef.current);
    dirtyRef.current = {};
    noteDirtyRef.current = {};
    clearTimeout(timerRef.current);
    const calls = [];
    ans.forEach(([qkey, value]) => calls.push(api.post(`/projects/${id}/responses`, { question_key: qkey, module: moduleKey, value }).then((r) => setSavedAt(r.data.saved_at)).catch(() => {})));
    nts.forEach(([qkey, note]) => calls.push(api.post(`/projects/${id}/question-note`, { question_key: qkey, module: moduleKey, note }).then((r) => setSavedAt(r.data.saved_at)).catch(() => {})));
    if (calls.length) await Promise.all(calls);
  }, [id, moduleKey]);

  const persist = useCallback((qkey, value) => {
    dirtyRef.current[qkey] = value;
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => { flushSaves(); }, 500);
  }, [flushSaves]);

  const persistNote = useCallback((qkey, note) => {
    noteDirtyRef.current[qkey] = note;
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => { flushSaves(); }, 500);
  }, [flushSaves]);

  // Flush any pending saves when leaving the module so nothing is lost.
  useEffect(() => () => {
    Object.entries(dirtyRef.current).forEach(([qkey, value]) => {
      api.post(`/projects/${id}/responses`, { question_key: qkey, module: moduleKey, value }).catch(() => {});
    });
    Object.entries(noteDirtyRef.current).forEach(([qkey, note]) => {
      api.post(`/projects/${id}/question-note`, { question_key: qkey, module: moduleKey, note }).catch(() => {});
    });
  }, [id, moduleKey]);

  const onChange = (value) => {
    setResponses((r) => ({ ...r, [current.key]: value }));
    persist(current.key, value);
    raiseFlagIfNeeded(current, value);
  };

  const raiseFlagIfNeeded = useCallback((q, value) => {
    if (!module || flags[q.key]) return;
    const exp = detectExpansion(q, value, module.scope);
    if (!exp) return;
    api.post(`/projects/${id}/scope-flags`, {
      question_key: q.key, module: moduleKey, question_text: q.text,
      trigger_label: exp.label, detected_scope: exp.scope,
    }).then((r) => {
      setFlags((f) => ({ ...f, [q.key]: r.data }));
      toast.info("Flagged as a potential scope expansion for consultant review");
    }).catch(() => {});
  }, [id, moduleKey, module, flags]);

  const classifyFlag = useCallback((qkey, status) => {
    const flag = flags[qkey];
    if (!flag) return;
    api.patch(`/projects/${id}/scope-flags/${flag.id}`, { status, note: flag.note || "" })
      .then((r) => { setFlags((f) => ({ ...f, [qkey]: r.data })); toast.success("Scope expansion classified"); })
      .catch(() => toast.error("Could not classify"));
  }, [id, flags]);

  const changeModuleScope = useCallback(async (val) => {
    await flushSaves();
    const next = { ...(data?.module_scope || {}) };
    if (val === "inherit") delete next[moduleKey];
    else next[moduleKey] = val;
    try {
      await api.patch(`/projects/${id}/scope`, { module_scope: next });
      setIdx(0);
      await loadDiscovery();
      await refresh();
      toast.success("Module scope updated");
    } catch { toast.error("Could not update module scope"); }
  }, [data, id, moduleKey, flushSaves, loadDiscovery, refresh]);

  const moduleScopeValue = (data?.module_scope || {})[moduleKey] || "inherit";
  const projectDefaultScope = data?.scope_level || "standard_config";

  const onNoteChange = (text) => {
    setNotes((n) => ({ ...n, [current.key]: text }));
    persistNote(current.key, text);
  };

  const removeCustom = async (qkey) => {
    await flushSaves();
    await api.delete(`/projects/${id}/custom-questions/${qkey}`);
    setIdx((i) => Math.max(0, i - 1));
    await loadDiscovery();
    toast.success("Question removed");
  };

  const applicable = vQs.filter((q) => q.type !== "info");
  const answeredCount = applicable.filter((q) => isAnswered(responses[q.key])).length;
  const totalCount = applicable.length;
  const percent = totalCount ? Math.round((answeredCount / totalCount) * 100) : 0;
  const requiredMissing = applicable.filter((q) => q.required && !isAnswered(responses[q.key]));

  if (!module) return <div className="flex flex-1 items-center justify-center text-slate-400">Loading module…</div>;

  if (module.out_of_scope) {
    return (
      <div className="px-6 py-8 lg:px-10">
        <div className="mx-auto max-w-2xl">
          <button onClick={() => navigate(`/projects/${id}/discovery`)} className="mb-4 flex items-center gap-1 text-xs font-semibold text-slate-400 hover:text-slate-600"><ArrowLeft className="h-3.5 w-3.5" /> Back to modules</button>
          <div data-testid="module-out-of-scope" className="rounded-2xl border border-amber-200 bg-amber-50 p-10 text-center">
            <Ban className="mx-auto h-10 w-10 text-amber-500" />
            <h1 className="mt-4 font-heading text-2xl font-extrabold text-slate-900">{module.title} is Out of Scope</h1>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">This area is marked out of scope for the agreed implementation, so its discovery questions are hidden. A consultant can adjust the scope strategy right here or from the project Overview.</p>
            {canManage ? (
              <div className="mt-6 flex flex-col items-center gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-slate-500">Change this module's scope:</span>
                  <ModuleScopeControl value={moduleScopeValue} projectDefault={projectDefaultScope} onChange={changeModuleScope} testid="oos-scope-control" />
                </div>
                <Button data-testid="oos-manage-scope" variant="outline" onClick={() => navigate(`/projects/${id}/overview`)} className="rounded-lg">Manage all scope on Overview</Button>
              </div>
            ) : (
              <Button data-testid="oos-manage-scope" onClick={() => navigate(`/projects/${id}/overview`)} className="mt-6 rounded-lg bg-sky-600 hover:bg-sky-700">Manage scope on Overview</Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ---- LEARN ----
  if (phase === PHASE.LEARN) {
    const learn = module.learn;
    return (
      <div className="px-6 py-8 lg:px-10">
        <div className="mx-auto max-w-3xl">
          <button onClick={() => navigate(`/projects/${id}/discovery`)} className="mb-4 flex items-center gap-1 text-xs font-semibold text-slate-400 hover:text-slate-600"><ArrowLeft className="h-3.5 w-3.5" /> Back to modules</button>
          {canManage && (
            <div className="mb-4 flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3">
              <span className="text-xs font-semibold text-slate-500">Discovery depth for this module:</span>
              <ModuleScopeControl value={moduleScopeValue} projectDefault={projectDefaultScope} onChange={changeModuleScope} testid="learn-scope-control" />
              <span className="text-[11px] text-slate-400">Higher scope reveals more detailed questions.</span>
            </div>
          )}
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
            <div className="brand-gradient relative p-8 text-white">
              <div className="absolute inset-0 subtle-grid opacity-20" />
              <div className="relative">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-xs font-bold uppercase tracking-wide backdrop-blur"><BookOpen className="h-3.5 w-3.5" /> Learn · Module {module.code}</span>
                <h1 className="mt-4 font-heading text-3xl font-black tracking-tight">{learn.title}</h1>
              </div>
            </div>
            <div className="p-8">
              <p className="text-[15px] leading-relaxed text-slate-700">{learn.intro}</p>

              {learn.flow && (
                <div className="mt-6 flex flex-wrap items-center gap-2">
                  {learn.flow.map((f, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <span className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-1.5 text-sm font-semibold text-sky-700">{f}</span>
                      {i < learn.flow.length - 1 && <ArrowRight className="h-4 w-4 text-slate-300" />}
                    </div>
                  ))}
                </div>
              )}

              <ul className="mt-6 space-y-2">
                {learn.points.map((p, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm text-slate-600"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" /> {p}</li>
                ))}
              </ul>

              {learn.images && learn.images.length > 0 && (
                <div className="mt-6 space-y-3">
                  <div className="text-xs font-bold uppercase tracking-wider text-slate-400">What this looks like in Salesforce</div>
                  {learn.images.map((img, i) => (
                    <figure key={i} className="overflow-hidden rounded-xl border border-slate-200">
                      <img src={img.url} alt={img.caption || "Salesforce reference"} loading="lazy" className="w-full" />
                      {img.caption && <figcaption className="bg-slate-50 px-4 py-2 text-xs text-slate-500">{img.caption}</figcaption>}
                    </figure>
                  ))}
                </div>
              )}

              {!learn.hide_video && (
                <div className="mt-6 flex items-center gap-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white shadow-sm"><PlayCircle className="h-6 w-6 text-sky-500" /></div>
                  <div className="flex-1">
                    <div className="text-sm font-bold text-slate-800">{learn.video_label}</div>
                    <div className="text-xs text-slate-400">Video walkthrough coming soon</div>
                  </div>
                </div>
              )}

              {canManage && module.custom_module && (
                <div className="mt-6 rounded-xl border border-violet-200 bg-violet-50/50 p-4" data-testid="custom-module-tools">
                  <div className="text-sm font-bold text-slate-800">Build this custom module</div>
                  <p className="mt-0.5 text-xs text-slate-500">{module.questions.length} question{module.questions.length === 1 ? "" : "s"} so far. Add your own or let AI suggest a starter set from the module name & description.</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <AddQuestionDialog projectId={id} module={moduleKey} onAdded={loadDiscovery} />
                    <AiSuggestDialog projectId={id} module={module} onAdded={loadDiscovery} />
                  </div>
                </div>
              )}

              <Button data-testid="continue-to-discovery" onClick={() => setPhase(PHASE.DISCOVER)} className="mt-8 h-12 w-full rounded-lg bg-sky-600 text-[15px] font-semibold hover:bg-sky-700">
                Continue to Discovery <ArrowRight className="ml-1.5 h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---- REVIEW ----
  if (phase === PHASE.REVIEW) {
    const complete = async () => {
      await flushSaves();
      if (requiredMissing.length > 0) {
        toast.error("Please answer all required questions before completing.");
        return;
      }
      await api.post(`/projects/${id}/module-status`, { module: moduleKey, status: "customer_completed" });
      await refresh();
      toast.success(`${module.title} marked complete`);
      navigate(`/projects/${id}/discovery`);
    };
    const cats = [];
    applicable.forEach((q) => { const c = q.category || q.section || "Other"; if (!cats.includes(c)) cats.push(c); });
    const editQuestion = (qKey) => {
      const i = vQs.findIndex((x) => x.key === qKey);
      if (i >= 0) { setIdx(i); setEditingFromReview(true); setShowHelp(false); setPhase(PHASE.DISCOVER); }
    };
    return (
      <div className="px-6 py-8 lg:px-10">
        <div className="mx-auto max-w-3xl">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-violet-50 px-3 py-1 text-xs font-bold uppercase tracking-wide text-violet-700"><ClipboardCheck className="h-3.5 w-3.5" /> Review</span>
          <h1 className="mt-3 font-heading text-2xl font-extrabold tracking-tight text-slate-900">Review your {module.title} requirements</h1>
          <p className="mt-1 text-sm text-slate-500">Check what we captured. You can edit any answer before marking this section complete.</p>

          <div className="mt-6 space-y-6">
            {cats.map((cat) => (
              <div key={cat} data-testid={`review-group-${cat.replace(/\s+/g, "-").toLowerCase()}`}>
                <div className="mb-2 text-xs font-bold uppercase tracking-wider text-sky-600">{cat}</div>
                <div className="space-y-3">
                  {applicable.filter((q) => (q.category || q.section) === cat).map((q) => (
                    <div key={q.key} data-testid={`review-${q.key}`} className={cn("rounded-xl border bg-white p-5", q.parent ? "ml-5 border-slate-100 bg-slate-50/60" : "border-slate-200")}>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 font-heading text-sm font-bold text-slate-800">{q.text}</div>
                        {q.custom && <span className="rounded-full bg-sky-50 px-2 py-0.5 text-[10px] font-bold uppercase text-sky-600">Custom</span>}
                        <button data-testid={`review-edit-${q.key}`} onClick={() => editQuestion(q.key)} className="inline-flex shrink-0 items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold text-sky-600 hover:bg-sky-50"><Pencil className="h-3.5 w-3.5" /> Edit</button>
                      </div>
                      <div className={cn("mt-2 text-sm", isAnswered(responses[q.key]) ? "text-slate-700" : "italic text-amber-500")}>
                        {isAnswered(responses[q.key]) ? formatValue(q, responses[q.key]) : (q.required ? "Required — not answered yet" : "Not answered")}
                      </div>
                      {isAnswered(notes[q.key]) && (
                        <div className="mt-2 flex items-start gap-1.5 rounded-lg bg-amber-50 p-2.5 text-xs text-amber-800"><StickyNote className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {notes[q.key]}</div>
                      )}
                      {flags[q.key] && <ScopeExpansionNotice flag={flags[q.key]} canManage={canManage} onClassify={classifyFlag} />}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {requiredMissing.length > 0 && (
            <div data-testid="review-required-warning" className="mt-6 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>Please answer {requiredMissing.length} required question{requiredMissing.length > 1 ? "s" : ""} before marking this section complete.</span>
            </div>
          )}

          <div className="mt-8 flex items-center justify-between border-t border-slate-100 pt-6">
            <Button variant="outline" data-testid="review-edit" onClick={() => { setPhase(PHASE.DISCOVER); setIdx(0); }} className="rounded-lg">
              <Pencil className="mr-1.5 h-4 w-4" /> Edit Answers
            </Button>
            <Button data-testid="review-complete" onClick={complete} disabled={requiredMissing.length > 0} className="rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed">
              <Check className="mr-1.5 h-4 w-4" /> Mark Section Complete
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ---- DISCOVER ----
  const next = async () => { await flushSaves(); setNoteOpen(false); if (editingFromReview) { setEditingFromReview(false); setShowHelp(false); setPhase(PHASE.REVIEW); return; } if (idx < vQs.length - 1) { setIdx(idx + 1); setShowHelp(false); } else setPhase(PHASE.REVIEW); };
  const back = async () => { await flushSaves(); setNoteOpen(false); if (editingFromReview) { setEditingFromReview(false); setShowHelp(false); setPhase(PHASE.REVIEW); return; } if (idx > 0) { setIdx(idx - 1); setShowHelp(false); } else setPhase(PHASE.LEARN); };
  if (!current) {
    if (module.questions.length === 0) {
      return (
        <div className="px-6 py-8 lg:px-10">
          <div className="mx-auto max-w-2xl rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center" data-testid="custom-module-empty">
            <h2 className="font-heading text-xl font-bold text-slate-900">No questions yet</h2>
            <p className="mt-2 text-sm text-slate-500">{canManage ? "Add questions manually or let AI suggest a starter set for this module." : "Your consultant is still setting up this module."}</p>
            {canManage && (
              <div className="mt-5 flex flex-wrap justify-center gap-2">
                <AddQuestionDialog projectId={id} module={moduleKey} onAdded={loadDiscovery} />
                {module.custom_module && <AiSuggestDialog projectId={id} module={module} onAdded={loadDiscovery} />}
              </div>
            )}
            <button onClick={() => setPhase(PHASE.LEARN)} className="mt-6 text-xs font-semibold text-slate-400 hover:text-slate-600">← Back to intro</button>
          </div>
        </div>
      );
    }
    return <div className="flex flex-1 items-center justify-center text-slate-400">Loading…</div>;
  }

  return (
    <div className="px-6 py-8 lg:px-10">
      <div className="mx-auto max-w-5xl">
        {/* header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <div className="font-heading text-lg font-bold text-slate-900">{module.title}</div>
                {canManage ? (
                  <ModuleScopeControl value={moduleScopeValue} projectDefault={projectDefaultScope} onChange={changeModuleScope} testid="module-scope-control" />
                ) : (
                  module.scope && <StatusBadge label={`${SCOPE_META[module.scope]?.label || module.scope} scope`} tone={SCOPE_META[module.scope]?.tone || "slate"} />
                )}
              </div>
              <div className="text-sm font-medium text-slate-400">{percent}% complete · {totalCount} question{totalCount === 1 ? "" : "s"} in scope</div>
            </div>
            <div className="flex items-center gap-3">
              {canManage && <AddQuestionDialog projectId={id} module={moduleKey} onAdded={loadDiscovery} />}
              <div className="flex items-center gap-2 text-xs font-medium text-slate-400">
                {savedAt ? <><Save className="h-3.5 w-3.5 text-emerald-500" /> Last saved: just now</> : <>Auto-saving enabled</>}
              </div>
            </div>
          </div>
          <Progress value={percent} className="mt-3 h-1.5" />
        </div>

        {["lead_management", "opportunity"].includes(moduleKey) && <OrgContextBox data={data} responses={responses} />}

        <div className="grid gap-6 lg:grid-cols-12">
          {/* Question card */}
          <div className={cn("lg:col-span-8", showHelp ? "" : "lg:col-span-12")}>
            <AnimatePresence mode="wait">
              <motion.div key={current.key} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.25 }}
                className="rounded-2xl border border-slate-200 bg-white p-8">
                <div className="flex items-center gap-2">
                  <div className="text-xs font-semibold uppercase tracking-wide text-sky-600">{current.category || current.section}</div>
                  {current.custom && <span className="rounded-full bg-sky-50 px-2 py-0.5 text-[10px] font-bold uppercase text-sky-600">Custom question</span>}
                </div>
                <h2 className="mt-2 font-heading text-2xl font-extrabold leading-snug tracking-tight text-slate-900">
                  {current.text}{current.required && <span className="text-red-400"> *</span>}
                </h2>
                {current.type === "multi_select" && <p className="mt-1 text-sm text-slate-400">Select all that apply.</p>}
                {current.example && current.type !== "info" && <p className="mt-1 text-sm italic text-slate-400">e.g. {current.example}</p>}
                {current.reference_image && (
                  <figure className="mt-4 overflow-hidden rounded-xl border border-slate-200">
                    <img src={current.reference_image.url} alt={current.reference_image.caption || "Salesforce reference"} loading="lazy" className="w-full" />
                    {current.reference_image.caption && <figcaption className="bg-slate-50 px-4 py-2 text-xs text-slate-500">{current.reference_image.caption}</figcaption>}
                  </figure>
                )}

                <div className="mt-6">
                  <QuestionRenderer question={{ ...current, module: moduleKey }} value={responses[current.key]} onChange={onChange} projectId={id} />
                </div>

                {flags[current.key] && <ScopeExpansionNotice flag={flags[current.key]} canManage={canManage} onClassify={classifyFlag} />}

                <div className="mt-6 border-t border-slate-100 pt-4">
                  {noteOpen || isAnswered(notes[current.key]) ? (
                    <div>
                      <div className="mb-1.5 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-slate-500"><StickyNote className="h-3.5 w-3.5" /> Specific detail / note</div>
                      <Textarea data-testid={`note-input-${current.key}`} value={notes[current.key] || ""} onChange={(e) => onNoteChange(e.target.value)} placeholder="Capture anything specific the customer said that the options above don't cover…" className="min-h-[90px] rounded-lg" />
                    </div>
                  ) : (
                    <button type="button" data-testid={`note-toggle-${current.key}`} onClick={() => setNoteOpen(true)} className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-sky-600">
                      <StickyNote className="h-4 w-4" /> Add a specific detail or note
                    </button>
                  )}
                </div>

                <div className="mt-4 flex items-center justify-between">
                  <button data-testid="why-asking" onClick={() => setShowHelp((s) => !s)} className="inline-flex items-center gap-1.5 text-sm font-semibold text-sky-600 hover:text-sky-700">
                    <HelpCircle className="h-4 w-4" /> Why are we asking this?
                  </button>
                  {current.custom && canManage && (
                    <button type="button" data-testid={`delete-custom-${current.key}`} onClick={() => removeCustom(current.key)} className="inline-flex items-center gap-1 text-xs font-semibold text-red-500 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /> Remove question</button>
                  )}
                </div>
              </motion.div>
            </AnimatePresence>

            <div className="mt-6 flex items-center justify-between">
              <Button variant="outline" data-testid="question-back" onClick={back} className="rounded-lg">
                {editingFromReview ? <><ClipboardCheck className="mr-1.5 h-4 w-4" /> Back to Review</> : <><ArrowLeft className="mr-1.5 h-4 w-4" /> Back</>}
              </Button>
              <Button data-testid="question-next" onClick={next} className="rounded-lg bg-sky-600 hover:bg-sky-700">
                {editingFromReview ? <>Save &amp; Back to Review <ClipboardCheck className="ml-1.5 h-4 w-4" /></> : (idx < vQs.length - 1 ? <>Save &amp; Continue <ArrowRight className="ml-1.5 h-4 w-4" /></> : <>Review Answers <ClipboardCheck className="ml-1.5 h-4 w-4" /></>)}
              </Button>
            </div>
          </div>

          {/* Help panel */}
          <AnimatePresence>
            {showHelp && (
              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }} className="lg:col-span-4">
                <div className="sticky top-40 rounded-2xl border border-sky-200 bg-sky-50 p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2 text-sky-700"><Lightbulb className="h-5 w-5" /><span className="font-heading text-sm font-bold">Why we ask</span></div>
                    <button onClick={() => setShowHelp(false)} className="text-sky-400 hover:text-sky-600"><X className="h-4 w-4" /></button>
                  </div>
                  <p className="mt-3 text-sm leading-relaxed text-slate-700">{current.help_text}</p>
                  {current.example && <div className="mt-4 rounded-lg bg-white/70 p-3 text-xs text-slate-600"><span className="font-semibold text-slate-500">Example: </span>{current.example}</div>}
                  <div className="mt-4 flex items-center gap-1.5 text-[11px] font-medium text-sky-500"><CheckCircle2 className="h-3.5 w-3.5" /> No Salesforce knowledge needed</div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
